const JOB_STATUS_COMPONENT_NAME = "JobStatus";

/** A glance window: the count line and three one-line workflow rows. */
export const JOB_STATUS_WINDOW_SIZE = { width: 360, height: 232 } as const;
/** Taller while a clicked row keeps its step list open; the width stays. */
export const JOB_STATUS_PINNED_SIZE = { width: JOB_STATUS_WINDOW_SIZE.width, height: 440 } as const;

type JobStatusComponentLike = {
  id: string;
  name: string;
  props?: Record<string, unknown>;
};

/** Resolve a live monitor's owning workspace without falling back to an unrelated window. */
export function findJobOutcomeWindow(windows: Record<string, readonly JobStatusComponentLike[]>, monitorId: string): string | null {
  const matches = Object.entries(windows).filter(([, components]) => components.some((component) => component.id === monitorId && component.name === JOB_STATUS_COMPONENT_NAME));
  return matches.length === 1 ? matches[0][0] : null;
}

type JobStatusReconciliation<T> = {
  components: T[];
  removed: T[];
  reusedExisting: boolean;
  changed: boolean;
};

const uniqueStrings = (values: unknown[]): string[] => Array.from(new Set(
  values.flatMap((value) => (
    Array.isArray(value)
      ? value.filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0)
      : []
  )),
));

/**
 * Keep one live JobStatus surface while allowing independent background-task
 * responses to contribute their job and Trigger run identifiers.
 */
export function reconcileJobStatusComponents<T extends JobStatusComponentLike>(
  components: T[],
  incoming?: T,
): JobStatusReconciliation<T> {
  const existingStatuses = components.filter(
    (component) => component.name === JOB_STATUS_COMPONENT_NAME,
  );

  if (existingStatuses.length === 0) {
    return {
      components,
      removed: [],
      reusedExisting: false,
      changed: false,
    };
  }

  const canonical = existingStatuses[0];
  const removed = existingStatuses.slice(1);
  const sources = incoming?.name === JOB_STATUS_COMPONENT_NAME
    ? [...existingStatuses, incoming]
    : existingStatuses;
  const mergedProps = sources.reduce<Record<string, unknown>>(
    (props, component) => ({ ...props, ...(component.props || {}) }),
    {},
  );
  const jobIds = uniqueStrings(sources.map((component) => component.props?.jobIds));
  const runIds = uniqueStrings(sources.map((component) => component.props?.runIds));

  const mergedCanonical = {
    ...canonical,
    props: {
      ...mergedProps,
      id: canonical.id,
      jobIds,
      runIds,
    },
  } as T;

  let replacedCanonical = false;
  const reconciled = components.flatMap((component) => {
    if (component.name !== JOB_STATUS_COMPONENT_NAME) return [component];
    if (component !== canonical) return [];
    replacedCanonical = true;
    return [mergedCanonical];
  });

  return {
    components: replacedCanonical ? reconciled : components,
    removed,
    reusedExisting: incoming?.name === JOB_STATUS_COMPONENT_NAME,
    changed: removed.length > 0 || incoming?.name === JOB_STATUS_COMPONENT_NAME,
  };
}

/**
 * A transferred JobStatus can be accepted without retaining its original
 * component id when the destination merged its tracked work into the local
 * singleton.
 */
export function isTransferredJobStatusRepresented<T extends JobStatusComponentLike>(
  components: T[],
  incoming: T,
): boolean {
  if (incoming.name !== JOB_STATUS_COMPONENT_NAME) return false;

  const incomingJobIds = uniqueStrings([incoming.props?.jobIds]);
  const incomingRunIds = uniqueStrings([incoming.props?.runIds]);

  return components.some((component) => {
    if (component.name !== JOB_STATUS_COMPONENT_NAME) return false;
    const representedJobIds = new Set(uniqueStrings([component.props?.jobIds]));
    const representedRunIds = new Set(uniqueStrings([component.props?.runIds]));
    return incomingJobIds.every((jobId) => representedJobIds.has(jobId))
      && incomingRunIds.every((runId) => representedRunIds.has(runId));
  });
}
